import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  getCubbyTiles, getCubbyLayoult, getChecksList,
  getCubbyDisplayColor,
  updateListOfColors,
} from '@reducers/cubby';
import { setNotification } from '@reducers/notification';
import Carousel from '@components/common/Carousel';
import Loader from '@components/common/Loader';
import cn from 'classnames';
import styles from './cubby.module.css';

// Methods

const useInterval = (callback, delay, parameter) => { 

  // State Management

  const savedCallback = useRef();
  const [color, setColor] = useState('bg-white');
  const intervalId = useRef(null);
  const clear = useCallback(() => clearInterval(intervalId.current), []);

  const timer = () => {
    const result = savedCallback.current();
    setColor(result);
    if (intervalId.current && !result) {
      clear();
    }
  };

  savedCallback.current = callback;

  useEffect(() => {
    if (intervalId.current) {
      clear();
    }

    if (delay) {
      intervalId.current = setInterval(timer, delay);
    }
    return () => {
      clear();
    };
  }, [parameter]);

  return [color];
};

const RenderComp = ({
  check,
  deviceInfo,
  listOfColors,
  updateColors,
  parentIndex,
  childIndex,
  rest
}) => {
  if (check) {
    const [style] = useInterval(() => {
      const color = getCubbyDisplayColor(check?.orders[0]?.slotAssignmentDate || check?.orders[0]?.pickupDate || check?.orders[0]?.orderDate, deviceInfo?.deviceGroup);
      return color;
    }, 1000, check);

    useEffect(() => {
      updateColors({
        id: rest?.id,
        color: style,
        checkId: check?.id,
        parentIndex,
        childIndex
      });
    }, [style]);
  }

  // HTML CSS Code

  return (
    <div
      className={cn('rounded w-100 px-0 h-100', styles.card, listOfColors[rest?.id]?.color)}
    >
      <div className={cn('h-100 w-100')}>
        <div className="py-2 px-1 text-capitalize fs-16 fw-600 col-12 row mx-0 justify-content-center align-items-center text-center border-bottom border-color-blue color-black">{rest.name}</div>
        <div className="py-2 px-1 col-12 text-center text-break text-capitalize">
          {check && (
            <>
              <div className="fs-14 py-1">#{check.number}</div>
              <div className="fs-12 py-1">{`${(check?.orders[0]?.checkInNote || '')} ${(check.name || check.stationName || check.stationNumber || '')}`}</div>
              {check?.deliver && !check?.marketplace && (
                <>
                  <div className="fs-12 py-1">{(check?.orders[0]?.deliveryVendorName || '')}</div>
                  <div className="fs-12 py-1">{(check?.orders[0]?.externalOrderNumber || '')}</div>
                </>
              )}
              {check?.marketplace && (
                <>
                  <div className="fs-12 py-1">{(check?.orders[0]?.marketplaceVendorName || '')}</div>
                  <div className="fs-12 py-1">{(check?.orders[0]?.marketplaceOrderId || '')}</div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Cubby = ({
  auth,
  cubbyLayoult,
  cubbyTilesList,
  checkListHashTable,
  deviceInfo,
  cubbyChecksList,
  listOfColors,
  ...props
}) => {

  // State Management

  const [isLandscape, setIsLandscape] = useState(window?.innerHeight < window?.innerWidth);
  const storeId = auth.id;
  const events = ['orientationchange', 'resize'];
  const cubbyRef = useRef(null);
  const cubbyIntervalId = useRef(null);

  // React Hooks
  
  useEffect(() => {
    if (storeId) {
      props.getCubbyLayoult(storeId);
      props.getCubbyTiles(storeId);
    }
    events.forEach((event) => {
      window.addEventListener(event, () => {
        setIsLandscape(window?.innerHeight < window?.innerWidth);
      }, false);
    });
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, () => {
          setIsLandscape(window?.innerHeight < window?.innerWidth);
        }, false);
      });
    };
  }, [storeId]);

  useEffect(() => {
    if (checkListHashTable) {
      refreshChecks();
    }
  }, [checkListHashTable]);

  useEffect(() => {
    if (cubbyChecksList?.length > 0 && cubbyRef.current) {
      if (cubbyIntervalId.current) {
        clearInterval(cubbyIntervalId.current);
      }
      cubbyIntervalId.current = setInterval(() => {
        const scrollDist = cubbyRef.current.scrollTop;
        const totalDist = +(cubbyRef.current.scrollHeight - cubbyRef.current.clientHeight);
        if (scrollDist < totalDist) {
          cubbyRef.current.scrollTop += 5;
        } else {
          cubbyRef.current.scrollTo(0, 0);
        }
      }, 200);
    }
  }, [cubbyChecksList]);

  // Methods

  const refreshChecks = () => {
    props.getChecksList({ checkListHashTable, deviceInfo });
  };

  const updateColors = (data) => {
    props.updateListOfColors(data);
  };
 
  return (
    <div className="col-12 h-100 w-100 p-1 row mx-0 background-primary overflowY">
      {props.loading && <Loader />}
      <div className={cn('col-12 px-1 h-100 overflowY row mx-0', isLandscape ? 'col-md-3' : 'col-md-12 order-last py-2')}>
        {cubbyTilesList?.map((e) => {
          return (
            <div className={cn('mx-0 mb-2 px-0 od-text-primary text-primary', isLandscape ? 'col-12' : 'col-6')}>
              {e.tileType === 7 ? (
                <Carousel imageUrls={e.imageUrls} style={{ imageSize: 'img-fluid mx-auto d-block' }} />
              ) : (
                <img src={e.imageUrl} className="img-fluid mx-auto d-block" alt="" />
              )}
            </div>
          );
        })}
      </div>
      <div ref={cubbyRef} className={cn('col-12 bg-lightblue rounded p-1 overflowY', isLandscape ? 'col-md-9 h-100' : 'col-md-12 order-first pb-3 maxH-100')}>
        {cubbyChecksList?.map((layoult, parentIndex) => {
          return (
            <div className="row mx-0 overflowX overflowYHidden w-100 align-items-stretch">
              {layoult.map(({ check, ...rest }, childIndex) => {
                return (
                  <div className={cn('col-6 col-md-3 col-lg-2 p-2')}>
                    <RenderComp
                      check={check}
                      rest={rest}
                      deviceInfo={deviceInfo}
                      parentIndex={parentIndex}
                      childIndex={childIndex}
                      updateColors={updateColors}
                      listOfColors={listOfColors}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Redux Part

const mapDispatchToProps = (dispatch) => bindActionCreators({
  getCubbyTiles,
  setNotification,
  getCubbyLayoult,
  getChecksList,
  updateListOfColors
}, dispatch);

const mapStateToProps = ({ cubby, oauth, storeChecks }) => ({
  loading: cubby.loading || storeChecks.loading,
  deviceInfo: oauth.deviceInformation,
  auth: oauth.authInfo,
  cubbyTilesList: cubby.cubbyTilesList,
  cubbyLayoult: cubby.cubbyLayoult,
  checkListHashTable: storeChecks.checkListHashTable,
  cubbyChecksList: cubby.cubbyChecksList,
  listOfColors: cubby.listOfColors
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Cubby);
